import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../../lib/api-client.js";
import {
  decodeFragment,
  rehypeHeadingIds,
  resolveSpecImage,
  resolveSpecLink,
  specAnchorId,
} from "./spec-link.js";

// The app header is sticky, so a plain scrollIntoView() parks the target
// underneath it. With `ifHidden`, only scroll when the target's top is out of
// sight (above the header's bottom edge).
function scrollBelowHeader(el: Element, ifHidden = false) {
  const header = document.querySelector(".app-header")?.getBoundingClientRect().height ?? 0;
  const top = el.getBoundingClientRect().top;
  if (ifHidden && top >= header) return;
  window.scrollTo({ top: window.scrollY + top - header - 8 });
}

export function SpecPanel({ owner, name }: { owner: string; name: string }) {
  const panelRef = useRef<HTMLElement>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Where to land once the view `open` switched to has rendered: the heading
  // a cross-doc link's `#fragment` names (its id doesn't exist until then),
  // else the top of the panel — not wherever the previous doc was scrolled to.
  const [landing, setLanding] = useState<{ fragment: string | null } | null>(null);
  const open = (file: string | null, fragment: string | null = null) => {
    setSelected(file);
    setLanding({ fragment });
  };

  const listQ = useQuery({
    queryKey: ["spec-files", owner, name],
    queryFn: () => api.listSpecFiles(owner, name),
  });

  const fileQ = useQuery({
    queryKey: ["spec-file", owner, name, selected],
    queryFn: () => api.getSpecFile(owner, name, selected!),
    enabled: selected !== null,
  });

  useEffect(() => {
    if (landing === null || (selected !== null && !fileQ.data)) return;
    const heading =
      landing.fragment === null ? null : document.getElementById(specAnchorId(landing.fragment));
    if (heading) scrollBelowHeader(heading);
    else if (panelRef.current) scrollBelowHeader(panelRef.current, true);
    setLanding(null);
  }, [landing, selected, fileQ.data]);

  if (listQ.isError) {
    return (
      <section className="panel">
        <div className="panel-header">
          <span>Spec docs</span>
        </div>
        <div style={{ padding: 16, color: "var(--err)" }}>
          Failed to load spec docs:{" "}
          {String((listQ.error as Error).message ?? listQ.error)}
        </div>
      </section>
    );
  }

  if (!listQ.data?.files.length) return null;
  const files = listQ.data.files;

  return (
    <section className="panel" ref={panelRef}>
      <div
        className="panel-header"
        style={{ display: "flex", alignItems: "center", gap: 12 }}
      >
        {selected && (
          <button
            onClick={() => open(null)}
            style={{
              background: "none",
              border: "none",
              color: "var(--accent)",
              cursor: "pointer",
              padding: 0,
              fontSize: 13,
            }}
          >
            ← Back
          </button>
        )}
        <span>{selected ?? `Spec docs (${listQ.data.files.length})`}</span>
      </div>

      {selected ? (
        fileQ.isLoading ? (
          <div style={{ padding: 16, color: "var(--muted)" }}>Loading…</div>
        ) : fileQ.isError ? (
          <div style={{ padding: 16, color: "var(--err)" }}>
            Failed to load {selected}:{" "}
            {String((fileQ.error as Error).message ?? fileQ.error)}
          </div>
        ) : (
          <div className="markdown" style={{ padding: "0 24px 24px" }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHeadingIds]}
              components={{
                img: ({ src, alt, title }) => (
                  <img
                    src={typeof src === "string" ? resolveSpecImage(src, owner, name) : undefined}
                    alt={alt}
                    title={title}
                  />
                ),
                a: ({ href, children }) => {
                  const link = resolveSpecLink(href ?? "", owner, name, files);
                  if (link.kind === "spec") {
                    return (
                      <a
                        href={link.href}
                        onClick={(e) => {
                          // Modified clicks keep the browser's new-tab behavior.
                          if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
                            return;
                          }
                          e.preventDefault();
                          open(link.file, link.fragment);
                        }}
                      >
                        {children}
                      </a>
                    );
                  }
                  if (link.kind === "external") {
                    return (
                      <a href={link.href} target="_blank" rel="noopener noreferrer">
                        {children}
                      </a>
                    );
                  }
                  if (!link.href.startsWith("#")) return <a href={link.href}>{children}</a>;
                  return (
                    <a
                      href={link.href}
                      onClick={(e) => {
                        // Heading ids carry rehypeHeadingIds' prefix, so the
                        // browser's own fragment jump wouldn't find them.
                        const fragment = decodeFragment(link.href);
                        if (fragment === null) return;
                        const target = document.getElementById(specAnchorId(fragment));
                        if (!target) return;
                        e.preventDefault();
                        scrollBelowHeader(target);
                      }}
                    >
                      {children}
                    </a>
                  );
                },
              }}
            >
              {fileQ.data?.content ?? ""}
            </ReactMarkdown>
          </div>
        )
      ) : (
        <div
          style={{ padding: 16, display: "flex", flexDirection: "column", gap: 6 }}
        >
          {listQ.data.files.map((f) => (
            <button
              key={f}
              onClick={() => open(f)}
              style={{
                background: "none",
                border: "1px solid var(--border)",
                borderRadius: 4,
                color: "var(--accent)",
                cursor: "pointer",
                padding: "8px 12px",
                textAlign: "left",
                fontSize: 13,
              }}
            >
              {f}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
